import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import FilterBar from "@/components/FilterBar";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import TableSkeleton from "@/components/TableSkeleton";
import RowActionsMenu from "@/components/RowActionsMenu";
import Banner from "@/components/Banner";
import { Check, X, RefreshCw, Repeat } from "lucide-react";

const STATUS_COLORS = {
  Pending:  "bg-amber-100   text-amber-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-rose-100    text-rose-700",
};

export default function AttendanceRegularizations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/attendance/regularizations", { params: statusFilter ? { status: statusFilter } : {} });
      setItems(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load regularizations");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const approve = async (id) => {
    try {
      await api.post(`/attendance/regularizations/${id}/approve`);
      setMsg("Regularization approved and attendance updated.");
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Approval failed"); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection? (optional)") || "";
    try {
      await api.post(`/attendance/regularizations/${id}/reject`, { reason });
      setMsg("Regularization rejected.");
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Rejection failed"); }
  };

  const { items: sortedItems, sortBy, sortDir, requestSort } = useSortableData(items, {
    initialKey: "submitted_at",
    initialDir: "desc",
  });

  return (
    <AdminLayout title="Regularization Requests" breadcrumbs={["Home", "Attendance", "Regularizations"]}>
      <div className="space-y-4">
        <Banner variant="error" onClose={() => setErr("")}>{err}</Banner>
        <Banner variant="success" onClose={() => setMsg("")}>{msg}</Banner>

        <FilterBar
          filters={[
            { type: "select", key: "status", label: "Status", value: statusFilter, onChange: setStatusFilter,
              options: [{ value: "", label: "All" }, ...["Pending", "Approved", "Rejected"].map((s) => ({ value: s, label: s }))] },
          ]}
          rightSlot={
            <button onClick={load} title="Refresh" data-testid="regs-refresh-btn"
              className="p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600">
              <RefreshCw className="h-4 w-4" />
            </button>
          }
        />

        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-x-auto">
          <div className="px-5 py-3 text-sm text-slate-500 border-b border-slate-200 flex items-center gap-2">
            <Repeat className="h-4 w-4" /> {sortedItems.length} request{sortedItems.length === 1 ? "" : "s"} {statusFilter ? `(${statusFilter})` : ""}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <SortableTH columnKey="employee_name" active={sortBy} dir={sortDir} onClick={requestSort}>Employee</SortableTH>
                <SortableTH columnKey="date" active={sortBy} dir={sortDir} onClick={requestSort}>Date</SortableTH>
                <SortableTH columnKey="current_status" active={sortBy} dir={sortDir} onClick={requestSort}>Current</SortableTH>
                <SortableTH columnKey="requested_status" active={sortBy} dir={sortDir} onClick={requestSort}>Requested</SortableTH>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">In / Out</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">Reason</th>
                <SortableTH columnKey="submitted_at" active={sortBy} dir={sortDir} onClick={requestSort}>Submitted</SortableTH>
                <SortableTH columnKey="status" active={sortBy} dir={sortDir} onClick={requestSort}>Status</SortableTH>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-600 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <TableSkeleton rows={4} cols={9} />
              ) : sortedItems.length === 0 ? (
                <EmptyState colSpan={9} message="No regularization requests in this view" />
              ) : sortedItems.map((r) => (
                <tr key={r.id} data-testid={`reg-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{r.employee_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDisplayDate(r.date)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.current_status || "—"}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{r.requested_status}</td>
                  <td className="px-4 py-3 text-slate-600 tabular-nums text-xs">
                    {(r.check_in || "—")}/{r.check_out || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.reason}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(r.submitted_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${STATUS_COLORS[r.status] || ""}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.status === "Pending" && (
                      <RowActionsMenu
                        testid={`reg-actions-${r.id}`}
                        items={[
                          { label: "Approve", icon: Check, onClick: () => approve(r.id), testid: `approve-reg-${r.id}` },
                          { label: "Reject",  icon: X,     danger: true, onClick: () => reject(r.id), testid: `reject-reg-${r.id}` },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
